import React from 'react';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { useNavigate } from 'react-router-dom';
import { useNotes } from '@/hooks/useNotes';
import { getMemberInfo } from '@/hooks/useMembers';
import MChip from '@/components/ui/atoms/MChip';
import MDocumentCard from '@/components/ui/atoms/MDocumentCard';
import MLoader from '@/components/ui/atoms/MLoader';
import styles from '../document.module.css';
import { formatDocumentDateShort } from '@/utils/date-utils';
import NoteDrawer from './NoteDrawer';
import { IconPlus, IconEdit, IconCircleX  } from '@tabler/icons-react';
import MOptionsMenu from '@/components/ui/atoms/MOptionsMenu';
import MDialog from '@/components/ui/atoms/MDialog';
import NoteUnreadBadge from '@/components/ui/atoms/NoteUnreadBadge';

const NotesListing: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const memberInfo = getMemberInfo();
const loggedInMemberId = JSON.parse(localStorage.getItem('member') || '{}').id;
const selectedMemberId = memberInfo.id || '';
const isSelf = loggedInMemberId === selectedMemberId;
const { notes, fetchNotes, createNote, fetchNote, updateNote, deleteNote, markAsReadNote, loading } = useNotes(selectedMemberId);

  // State for edit/delete
  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null);
  const [editingNoteLoading, setEditingNoteLoading] = React.useState(false);
  const [editingNoteData, setEditingNoteData] = React.useState<any>(null);
  const [deletingNoteId, setDeletingNoteId] = React.useState<string | null>(null);


  const handleNoteDrawerDone = async (fields: { type: 'personal' | 'family'; title: string; content: string }) => {
    // Map drawer type to backend type
    let backendType: 'personal' | 'family' = 'personal';
    if (fields.type === 'family') backendType = 'family';
    await createNote({
      memberId: selectedMemberId,
      title: fields.title,
      content: fields.content,
      type: backendType,
    });
    //toast.showToast({ message: 'Note added', variant: 'success' });
    await fetchNotes({ type: 'personal' }); // refresh all notes
    setShowDrawer(false);
  };


  // Always fetch only personal notes
  React.useEffect(() => {
    fetchNotes({ type: 'personal' });
  }, [fetchNotes]);

  // Handler for NoteDrawer done (edit or add)
  const handleDrawerDone = async (fields: { type: 'personal' | 'family'; title: string; content: string }) => {
    if (editingNoteId) {
      await updateNote(editingNoteId, fields);
      //toast.showToast({ message: 'Note updated', variant: 'success' });
      setEditingNoteId(null);
      setEditingNoteData(null);
    } else {
      await handleNoteDrawerDone(fields);
    }
    await fetchNotes({ type: 'personal' });
    setShowDrawer(false);
  };

  // Handler for closing NoteDrawer
  const handleDrawerClose = () => {
    setEditingNoteId(null);
    setEditingNoteData(null);
    setShowDrawer(false);
  };

  // Handler for confirming delete
  const handleDeleteConfirm = async () => {
    if (deletingNoteId) {
      await deleteNote(deletingNoteId);
      //toast.showToast({ message: 'Note deleted', variant: 'success' });
      setDeletingNoteId(null);
      await fetchNotes({ type: 'personal' });
    }
  };

  // Handler for canceling delete
  const handleDeleteCancel = () => {
    setDeletingNoteId(null);
  };

  return (
    <>
      <div>
        <div className={styles.notesListingContainer}>
          <div className={styles.notesListingHeaderRow}>
            <h1 className={styles.notesListingTitle}>Notes</h1>
            {isSelf && (
  <button
    className={styles.notesListingAddButton}
    aria-label="Add Note"
    onClick={() => setShowDrawer(true)}
  >
    <IconPlus size={24} stroke={2} />
  </button>
)}
          </div>
          {loading ? (
            <MLoader />
          ) : (
            notes.map(note => (
              <MDocumentCard
                key={note.id}
                insights={false} // Notes don't have insights
                displayName={
                  note.type === 'family' && note.unreadCommentsCount !== 0 ? (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {note.title}
                      <NoteUnreadBadge count={note.unreadCommentsCount} />
                    </span>
                  ) : (
                    note.title
                  )
                }
                meta={note.updatedAt ? formatDocumentDateShort(note.updatedAt) : ''}
                category={note.type}
                onClick={async () => {
  if (note.type === 'family') {
    if (note.unreadCommentsCount !== 0) {
      await markAsReadNote(note.id);
    }
    navigate(`/family-notes/${note.id}`);
  } else {
    setEditingNoteLoading(true);
    setEditingNoteId(note.id);
    fetchNote(note.id)
      .then(data => setEditingNoteData(data))
      .finally(() => setEditingNoteLoading(false));
  }
}}
                optionsMenu={
                  isSelf && loggedInMemberId === note.ownerId ? (
                    <div onClick={e => e.stopPropagation()}>
                      <MOptionsMenu
                        options={[
                          {
                            id: 'edit',
                            label: 'Edit',
                            icon: <IconEdit />,
                            onClick: async () => {
                              setEditingNoteLoading(true);
                              setEditingNoteId(note.id);
                              try {
                                const data = await fetchNote(note.id);
                                setEditingNoteData(data);
                              } finally {
                                setEditingNoteLoading(false);
                              }
                            }
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            icon: <IconCircleX  />,
                            delete: true,
                            onClick: () => setDeletingNoteId(note.id)
                          }
                        ]}
                      />
                    </div>
                  ) : null
                }
              />
            ))
          )}
        </div>
        {/* Add/Edit Note Drawer */}
        <NoteDrawer
          open={showDrawer || !!editingNoteId}
          onClose={handleDrawerClose}
          onDone={handleDrawerDone}
          loading={editingNoteLoading}
          noteId={editingNoteId || undefined}
          initialData={editingNoteData}
          mode={editingNoteId ? 'edit' : 'add'}
          onDelete={editingNoteId ? () => setDeletingNoteId(editingNoteId) : undefined}
          noteType="personal"
        />

        {/* Delete Confirmation Dialog */}
        <MDialog
          isOpen={!!deletingNoteId}
          onClose={handleDeleteCancel}
          title="Delete Note"
          description="Are you sure you want to delete this note? This action cannot be undone."
          primaryCtaText="Delete"
          onPrimaryCta={handleDeleteConfirm}
          secondaryCtaText="Cancel"
          onSecondaryCta={handleDeleteCancel}
          destructivePrimary
        />
      </div>
    </>
  );
};

export default NotesListing;
